import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { wishlistAPI } from '../api';
import { useAuth } from './AuthContext';

const WishlistContext = createContext(null);

const LOCAL_KEY = 'lunora_wishlist_anon';

const loadLocal = () => {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY)) || []; } catch { return []; }
};
const saveLocal = (items) => {
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(items)); } catch {}
};

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load on mount + when auth changes
  const load = useCallback(async () => {
    if (user && user.id) {
      setLoading(true);
      try {
        const list = await wishlistAPI.list();
        setItems(list);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    } else {
      setItems(loadLocal());
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Sync anonymous wishlist to server on login
  useEffect(() => {
    const sync = async () => {
      if (!user || !user.id) return;
      const anon = loadLocal();
      if (anon.length === 0) return;
      try {
        await Promise.all(anon.map(a => wishlistAPI.add(a).catch(() => null)));
        saveLocal([]);
        await load();
      } catch {}
    };
    sync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const has = useCallback((productId) => {
    return items.some(i => i.product_id === productId);
  }, [items]);

  const add = async (product) => {
    if (user && user.id) {
      try {
        const saved = await wishlistAPI.add(product);
        setItems(prev => prev.some(i => i.product_id === saved.product_id) ? prev : [saved, ...prev]);
        return saved;
      } catch (e) {
        return null;
      }
    }
    // anonymous
    const localItem = { ...product, id: `loc-${product.product_id}`, created_at: new Date().toISOString() };
    setItems(prev => {
      if (prev.some(i => i.product_id === product.product_id)) return prev;
      const next = [localItem, ...prev];
      saveLocal(next);
      return next;
    });
    return localItem;
  };

  const remove = async (productId) => {
    if (user && user.id) {
      try { await wishlistAPI.remove(productId); } catch {}
    }
    setItems(prev => {
      const next = prev.filter(i => i.product_id !== productId);
      if (!user || !user.id) saveLocal(next);
      return next;
    });
  };

  const toggle = async (product) => {
    if (has(product.product_id)) await remove(product.product_id);
    else await add(product);
  };

  return (
    <WishlistContext.Provider value={{ items, loading, has, add, remove, toggle, reload: load }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider');
  return ctx;
};
